import { Button, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import LabelReportButtonGenerate from 'components/molecules/buttons/reportGenerate/LabelReportButtonGenerate';
import LabelReportButtonDownload from 'components/molecules/buttons/reportDownload/LabelReportButtonDownload';
import SendButtonEmail from 'components/molecules/buttons/sendEmail/SendButtonEmail';
import LabeledEmailSend from 'components/molecules/labeledStatus/LabeledEmailSend';
import DownloadFilesInPeriodPopup from 'components/molecules/popup/download-files-in-period/DownloadFilesInPeriodPopup';
import { FooterTable, QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { FormikHelpers } from 'formik';
import { useComponentVisible, useHasPermission } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useNavigate } from 'react-router-dom';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiClock, PiNavigationArrow, PiTrash } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { DOCUMENT_TYPE } from 'services/email/types';
import {
  useDeleteReport,
  useDeleteReportPeriod,
  useGenerateLabelReportDetailedFile,
  useGenerateLabelReportFile,
  useGetLabelReportFile,
  useGetLabelReportFiles,
  useGetLabelReportsInfinite,
  useGetLabelReportsSearch,
} from 'services/label-report';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { EMAIL_SEND } from 'services/types';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { object, string } from 'yup';

enum SORT_COLUMNS {
  startSettlementPeriod,
  endSettlementPeriod,
  label,
  number,
  net,
  compliance,
}

interface IGlobalReportsSearch {
  startSettlementPeriodId: string;
  endSettlementPeriodId: string;
}

const globalReportsSearchInitialValues: IGlobalReportsSearch = {
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
};

const Raport = () => {
  usePermissionGuard('LabelReportPermissionsLIST**', '/', true, 'statements');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();

  const { setFile } = useStreamFileDownload(useGetLabelReportFile);

  const columnsVisibility = useTableColumnsVisibility(
    'GlobalRaports3',
    SORT_COLUMNS
  );

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters =
    searchById[columnsVisibility.tableId] || globalReportsSearchInitialValues;

  const i18nextPrefix = 'navbar.raport';

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);

  const remove = useDeleteReport();
  const removePeriod = useDeleteReportPeriod();
  const generateLabelReportFile = useGenerateLabelReportFile();
  const generateLabelReportDetailedFile = useGenerateLabelReportDetailedFile();
  const getSettelmentPeriod = useGetSettlementLightList();

  const settlementPeriods = useGetSettlementLightList();

  const labelReportsQuery = useGetLabelReportsInfinite({
    ...(filters.startSettlementPeriodId && {
      startSettlementPeriodDate: filters.startSettlementPeriodId,
    }),
    ...(filters.endSettlementPeriodId && {
      endSettlementPeriodDate: filters.endSettlementPeriodId,
    }),
  });
  const labelReportsStatistics = useGetLabelReportsSearch({
    ...(filters.startSettlementPeriodId && {
      startSettlementPeriodDate: filters.startSettlementPeriodId,
    }),
    ...(filters.endSettlementPeriodId && {
      endSettlementPeriodDate: filters.endSettlementPeriodId,
    }),
  });

  const netAmountLength = formatCurrency(
    labelReportsStatistics.data?.statistics.allAmountEarned
  ).length;

  const hasDetailPermission = hasPermission('LabelReportPermissionsLIST**', 16);
  const navigate = useNavigate();

  const tableProps = useInfiniteTable(labelReportsQuery, {
    selectable: false,
  });

  const goToReport = (id: string, labelId?: string) => {
    const path = labelId ? `/label/${labelId}/reports/${id}` : '/label-reports';
    navigate(path, { state: 'raport' });
  };

  const settlementPeriodOptions =
    settlementPeriods.data?.flat().map(period => ({
      label: getSettlementPeriodName(period.settlementPeriodName),
      value: period.id,
    })) ?? [];

  const onRemoveRaport = async (actions: PopupActions, id: string) =>
    await remove.mutateAsync(id, {
      onSuccess: async () => {
        await labelReportsQuery.refetch();
        await labelReportsStatistics.refetch();
        actions.close();
      },
      onError: error => onErrorShowAllErrorsToast(error),
    });
  const onRemovePeriodRaports = async (
    values: { id: string },
    helpers: FormikHelpers<{ id: string }>,
    actions: PopupActions | null
  ) =>
    await removePeriod.mutateAsync(values.id, {
      onSuccess: async () => {
        await labelReportsQuery.refetch();
        await labelReportsStatistics.refetch();
        actions?.close();
      },
      onError: error => onErrorShowAllErrorsToast(error),
    });

  const onGenerateLabelReportFile = async (labelReportId: string) =>
    await generateLabelReportFile.mutateAsync(
      { labelReportId },
      {
        onSuccess: async () => await labelReportsQuery.refetch(),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );

  const onGenerateLabelReportDetailedFile = async (labelReportId: string) =>
    await generateLabelReportDetailedFile.mutateAsync(
      { labelReportId },
      {
        onSuccess: async () => await labelReportsQuery.refetch(),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );

  const getSettlementPeriodOptions = () =>
    getSettelmentPeriod.data?.flat().map(settelment => ({
      value: settelment.id,
      label: getSettlementPeriodName(settelment.settlementPeriodName),
    }));

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`navbar.raport`)}
          hideBackButton
          topbarContent={
            <>
              <div className='flex gap-2'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix='navbar.raport'
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={SORT_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix='input.label'
                  customI18next=''
                />
                <div />
                <DownloadFilesInPeriodPopup
                  service={useGetLabelReportFiles}
                  filePrefix={t('navbar.raport')}
                />
                <MagellanPopups.Form
                  header={t('label-reports.remove-period')}
                  submitButton={
                    <Button type='submit' variant={BUTTON_VARIANT.remove}>
                      <Button.Icon icon={<PiTrash />} />
                      <Button.Text text={t('button.remove')} />
                    </Button>
                  }
                  trigger={
                    <Button isClear onlyIcon variant={BUTTON_VARIANT.remove}>
                      <Button.Description description={t('button.remove')} />
                      <Button.Icon icon={<PiTrash />} />
                    </Button>
                  }
                  initialValues={{ id: '' }}
                  validationSchema={object().shape({
                    id: string().required(t('formik-error.required')),
                  })}
                  onSubmit={onRemovePeriodRaports}>
                  <FormInput.Select
                    label={t('input.label.settlementPeriod')}
                    name='id'
                    options={getSettlementPeriodOptions()}
                    isAsync
                  />
                </MagellanPopups.Form>
              </div>
            </>
          }>
          <QueryFilter<IGlobalReportsSearch>
            i18nextPrefix='navbar.raport'
            initialValues={filters}>
            <>
              <QueryFilter.Form<IGlobalReportsSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(columnsVisibility.tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='startSettlementPeriodId'
                    label={t('input.label.startSettlementPeriod')}
                    options={settlementPeriodOptions}
                    isAsync
                  />
                  <FormInput.Select
                    name='endSettlementPeriodId'
                    label={t('input.label.endSettlementPeriod')}
                    options={settlementPeriodOptions}
                    isAsync
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data => {
                  const period = settlementPeriods.data
                    ?.flat()
                    .find(p => p.id === data.value);
                  return period
                    ? getSettlementPeriodName(period.settlementPeriodName)
                    : `${data.value}`;
                }}
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <FooterTable
        {...tableProps}
        id={columnsVisibility.tableId}
        columns={[
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: ({ startSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(startSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.startSettlementPeriod),
            footer: t('table.total'),
          },
          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: ({ endSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(endSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.endSettlementPeriod),
          },
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.label),
          },
          {
            label: t('input.label.number'),
            renderCell: ({ labelReportNumber }) => (
              <TableItem>
                <TableItem.Text text={labelReportNumber} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.number),
          },
          {
            label: t('input.label.net'),
            renderCell: item => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(item.amountEarned, {
                    length: netAmountLength,
                  })}
                />
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.net),
            footer: formatCurrency(
              labelReportsStatistics.data?.statistics.allAmountEarned
            ),
            resize: true,
          },
          {
            label: t('label.table.compliance'),
            renderCell: ({ isOutdated }) => (
              <TableItem justifyPosition='center'>
                {isOutdated && (
                  <TableItem.Label color='orange' size='compact'>
                    <PiClock />
                    {t('label.compliance.outdated')}
                  </TableItem.Label>
                )}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.compliance),
          },
          {
            label: t('input.label.email-sender'),
            renderCell: ({ id, sendStatus }) => (
              <TableItem>
                <div className='flex items-center gap-2'>
                  {sendStatus !== EMAIL_SEND.notSend && (
                    <LabeledEmailSend status={sendStatus} />
                  )}
                  {sendStatus !== EMAIL_SEND.planned && (
                    <SendButtonEmail
                      documentId={id}
                      documentType={DOCUMENT_TYPE.labelReport}
                      refetch={labelReportsQuery.refetch}
                      status={sendStatus}
                    />
                  )}
                </div>
              </TableItem>
            ),
            resize: true,
          },
          {
            label: '',
            renderCell: ({
              id,
              labelId,
              labelReportNumber,
              canGenerateDetailedReport,
              detailedFilePath,
            }) => (
              <TableItem justifyPosition='end'>
                <div className='flex items-center gap-1'>
                  <LabelReportButtonGenerate
                    id={id}
                    alignTop={
                      (labelReportsQuery.data?.pages.flat().reverse()[0])?.id === id
                    }
                    canGenerateDetailedReport={canGenerateDetailedReport}
                    onGenerateSimple={onGenerateLabelReportFile}
                    onGenerateDetailed={onGenerateLabelReportDetailedFile}
                    isLoadingSimple={
                      generateLabelReportFile.isPending &&
                      generateLabelReportFile.variables.labelReportId === id
                    }
                    isLoadingDetailed={
                      generateLabelReportDetailedFile.isPending &&
                      generateLabelReportDetailedFile.variables.labelReportId === id
                    }
                  />
                  {hasDetailPermission && (
                      <LabelReportButtonDownload
                        id={id}
                        name={labelReportNumber ?? ''}
                        alignTop={
                          (labelReportsQuery.data?.pages.flat().reverse()[0])?.id === id
                        }
                        canGenerateDetailedReport={!!detailedFilePath}
                      />                     
                  )}
                  <MagellanPopups.DeleteWarning
                    name={t('label-reports.what')}
                    onRemove={actions => onRemoveRaport(actions, id)}
                  />
                  <Button
                    type='button'
                    isClear
                    onlyIcon
                    onClick={() => goToReport(id, labelId)}>
                    <Button.Description description={t('button.open')} />

                    <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                </div>
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Raport;
